import React, { useEffect, useState } from "react";
import { Badge, Button, Tooltip, Collapse, Typography, Tag, Space } from "antd";
import {
  CheckCircleFilled,
  FilterOutlined,
  UndoOutlined,
  WarningOutlined,
  FolderOutlined,
  CaretRightOutlined,
} from "@ant-design/icons";
import SplitPlanModal from "../modal/SplitPlanModal";
import CascadePlanModal from "../modal/CascadePlanModal";
import { useLocation, useParams } from "react-router-dom";
import PageLoader from "../sub/PageLoader";
import GetSplitPlanApi from "../../api/split-plans/GetSplitPlanApi";
import GetCascadedPlansApi from "../../api/plans/GetCascadedPlansApi";
import { useSelector } from "react-redux";

import SplitsFilterModal from "../modal/filters/SplitsFilterModal";
import { permissionAbility } from "../../helpers/permissionHelpers";
import SplitPlanTreeCards from "./SplitPlanTreeCards";
import GetFilteredSplitPlanApi from "../../api/split-plans/GetFilteredSplitPlanAPI";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

const { Panel } = Collapse;
const { Text } = Typography;

function TopPlanPlans(props) {
  const [canStart, setCanStart] = useState(false);
  const [canShow, setCanShow] = useState(false);
  const [data, setData] = useState([]);
  const [cascadeData, setCascadeData] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [groupedData, setGroupedData] = useState([]);
  const [addModal, setAddModal] = useState(false);
  const [addCascadeModal, setAddCascadeModal] = useState(false);
  const [filterModal, setFilterModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [filterBy, setFilterBy] = useState();
  const proHeight = document.body.clientHeight;
  let curLocation = useLocation();
  const positionState = useSelector((state) => state.positionState.value);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const userLevel = positionState?.userRole?.name;
  const [startAt, setStartAt] = useState(0);
  const [totalSplitPlanCount, setTotalSplitPlanCount] = useState(0);
  const { id } = useParams();
  const limit = LIMIT_PER_PAGE;

  useEffect(() => {
    // Reset data when location changes to prevent duplication
    setData([]);
    setCascadeData([]);
    setGroupedData([]);
    setStartAt(0);
    
    getPlans(0, true); // Use newSet=true to replace data
    // CASCADE PLANS SHOULD NOT BE SHOWN IN PLANS TAB - REMOVED
    // getCascadePlans(0, true); // Use newSet=true to replace data
    fetchStrategies();
  }, [curLocation]);

  // Re-group data when strategies change - prevent unnecessary re-grouping
  useEffect(() => {
    if (data.length > 0 && strategies.length >= 0) {
      // ONLY show split plans, NOT cascade plans
      const allPlans = [
        ...data.map(plan => ({ ...plan, planType: 'Split Plan' }))
      ];
      const grouped = groupPlansByStrategy(allPlans, strategies);
      setGroupedData(grouped);
    } else if (data.length === 0) {
      // Clear grouped data when no plans exist
      setGroupedData([]);
    }
  }, [data, strategies]); // Removed cascadeData dependency

  async function fetchStrategies() {
    try {
      const token = 'Bearer ' + localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}v1/strategies/plan/${id}`,
        {
          headers: {
            Authorization: token,
          },
        }
      );
      const strategiesData = await response.json();

      if (strategiesData.success) {
        setStrategies(strategiesData.data || []);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    }
  }

  function groupPlansByStrategy(plans, strategies) {
    
    const grouped = [];
    const usedPlanIds = new Set();

    // Group plans by strategy
    strategies.forEach(strategy => {
      const strategyPlans = plans.filter(plan => {
        const planStrategyId = plan.strategyId;
        const strategyId = strategy.id;
        
        // Handle different data types for comparison
        if (!planStrategyId) return false;
        
        // Try exact match first, then string comparison, then number comparison
        return planStrategyId === strategyId || 
               String(planStrategyId) === String(strategyId) ||
               (Number(planStrategyId) === Number(strategyId) && !isNaN(planStrategyId) && !isNaN(strategyId));
      });
      
      if (strategyPlans.length > 0) {
        strategyPlans.forEach(plan => usedPlanIds.add(plan.planId || plan.id));
        grouped.push({
          type: 'strategy',
          strategy: strategy,
          plans: strategyPlans,
          key: `strategy-${strategy.id}`
        });
      }
    });

    // Add ungrouped plans
    const ungroupedPlans = plans.filter(plan => {
      const hasStrategy = plan.strategyId && plan.strategyId !== null && plan.strategyId !== '';
      const isUsed = usedPlanIds.has(plan.planId || plan.id);
      return !hasStrategy || !isUsed;
    });
    
    if (ungroupedPlans.length > 0) {
      grouped.push({
        type: 'ungrouped',
        strategy: {
          id: 'ungrouped',
          name: 'Ungrouped Plans',
          description: 'Plans not assigned to any strategy'
        },
        plans: ungroupedPlans,
        key: 'ungrouped'
      });
    }

    return grouped;
  }

  async function getPlans(num, newSet) {
    setTableLoading(true);
    const apiResponse = await GetSplitPlanApi(id, num, limit);

    if (apiResponse.success) {
      if (apiResponse.data?.plans?.length !== 0) {
        let newData;
        if (newSet) {
          newData = apiResponse.data?.plans || [];
          setData(newData);
        } else {
          // Prevent duplicates by filtering out existing plans
          const existingPlanIds = new Set(data.map(plan => plan.planId || plan.id));
          const newPlans = (apiResponse.data?.plans || []).filter(plan => 
            !existingPlanIds.has(plan.planId || plan.id)
          );
          newData = [...data, ...newPlans];
          setData(newData);
        }

        if (newData.length > 0) {
        }

        setCanShow(true);
        setTotalSplitPlanCount(apiResponse.data?.totalCount);
      }
    }
    setCanStart(true);
    setTableLoading(false);
  }

  async function getCascadePlans(num, newSet) {
    try {
      // Use the correct performance cycle from Redux store
      const apiResponse = await GetCascadedPlansApi(perfCycleState.id, id, positionState?.id, num, limit);

      console.log('🔍 [TopPlanPlans] getCascadePlans API Response:', apiResponse);
      
      if (apiResponse.success) {
        if (apiResponse.data?.plans?.length !== 0) {
          // Debug the first plan to see if percentage fields are present
          if (apiResponse.data?.plans?.length > 0) {
            const firstPlan = apiResponse.data.plans[0];
            console.log('🔍 [TopPlanPlans] First cascade plan data:', {
              planId: firstPlan.planId,
              title: firstPlan.title,
              isPercentageUpdate: firstPlan.isPercentageUpdate,
              ancestorHasPercentageUpdate: firstPlan.ancestorHasPercentageUpdate,
              allFields: Object.keys(firstPlan)
            });
          }
          
          let newData;
          if (newSet) {
            newData = apiResponse.data?.plans || [];
            setCascadeData(newData);
          } else {
            // Prevent duplicates by filtering out existing plans
            const existingPlanIds = new Set(cascadeData.map(plan => plan.planId || plan.id));
            const newPlans = (apiResponse.data?.plans || []).filter(plan => 
              !existingPlanIds.has(plan.planId || plan.id)
            );
            newData = [...cascadeData, ...newPlans];
            setCascadeData(newData);
          }

          if (newData.length > 0) {
          }

          setCanShow(true);
        }
      }
    } catch (error) {
      console.error('Error fetching cascade plans:', error);
    }
  }

  async function getFilteredPlans(values, startAt, newSet) {
    setTableLoading(true);
    let path = curLocation.pathname;
    let id = path.split("/top-plans/");
    const apiResponse = await GetFilteredSplitPlanApi(
      id[1],
      values.planLevel,
      startAt,
      limit
    );
    if (apiResponse.success) {
      setFilterStatus(true);
      setTableLoading(false);
      setFilterBy({
        title: values.title,
        planLevel: values.planLevel,
      });

      let newData;
      if (newSet) {
        newData = apiResponse.data?.plans;
        setData(newData);
      } else {
        newData = [...data, ...apiResponse.data?.plans];
        setData(newData);
      }

      // Group plans by strategy
      const grouped = groupPlansByStrategy(newData, strategies);
      setGroupedData(grouped);

      setTotalSplitPlanCount(apiResponse.data?.totalCount);
      if (apiResponse.data?.plans?.length !== 0) {
        setCanShow(true);
      }
    }
    setCanStart(true);
  }

  function showEmptyPlans() {
    return (
      <div
        style={{ height: proHeight - 188 }}
        className="flex justify-center items-center w-full h-full"
      >
        <div className="text-center">
          <div className="text-3xl text-gray-400">
            <WarningOutlined />
          </div>
          <div className="text-xl pri-font text-gray-400">
            No plans available
          </div>
          {permissionAbility(userLevel, "plans", "split") && (
            <>
              <div className="mb-3 text-gray-400">Create plans for this plan</div>
              <Button onClick={() => setAddModal(true)} type="primary">
                Split Plan
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  function resetFilter() {
    setData([]);
    setCascadeData([]);
    setFilterStatus(false);

    getPlans(0, true);
    // CASCADE PLANS SHOULD NOT BE SHOWN IN PLANS TAB - REMOVED
    // getCascadePlans(0, true);
    setFilterBy();
  }

  function loadMore(num) {
    if (filterStatus) {
      getFilteredPlans(filterBy, num);
    } else {
      getPlans(num);
      // CASCADE PLANS SHOULD NOT BE SHOWN IN PLANS TAB - REMOVED
      // getCascadePlans(num);
    }
  }
  function handleDelete(planId, planType) {
    setTotalSplitPlanCount(totalSplitPlanCount - 1);

    if (planType === 'Split Plan') {
      const newData = [];
      data.forEach((element) => {
        if (planId !== element.planId) {
          newData.push(element);
        }
      });
      setData(newData);
    } else if (planType === 'Cascade Plan') {
      const newCascadeData = [];
      cascadeData.forEach((element) => {
        if (planId !== element.planId) {
          newCascadeData.push(element);
        }
      });
      setCascadeData(newCascadeData);
    }

    if (totalSplitPlanCount - 1 === 0) {
      setCanShow(false);
    }
  }
  async function handleEdit(editedPlan) {
    // First refresh strategies in case new ones were created
    await fetchStrategies();
    
    if (editedPlan.planType === 'Split Plan') {
      const newData = [];
      data.forEach((element) => {
        if (editedPlan.planId === element.planId) {
          newData.push(editedPlan);
        } else {
          newData.push(element);
        }
      });
      setData(newData);
      
      // If cascade setting changed, refresh parent plan data
      if (props.onParentRefresh && typeof props.onParentRefresh === 'function') {
        props.onParentRefresh();
      }
      
      // Re-group data after edit with updated strategies
      // Note: we'll use the updated strategies from the state after fetchStrategies completes
      setTimeout(() => {
        const allPlans = [
          ...newData.map(plan => ({ ...plan, planType: 'Split Plan' })),
          ...cascadeData.map(plan => ({ ...plan, planType: 'Cascade Plan' }))
        ];
        const grouped = groupPlansByStrategy(allPlans, strategies);
        setGroupedData(grouped);
      }, 100);
    } else if (editedPlan.planType === 'Cascade Plan') {
      const newCascadeData = [];
      cascadeData.forEach((element) => {
        if (editedPlan.planId === element.planId) {
          newCascadeData.push(editedPlan);
        } else {
          newCascadeData.push(element);
        }
      });
      setCascadeData(newCascadeData);
      
      // Re-group data after edit with updated strategies
      setTimeout(() => {
        const allPlans = [
          ...data.map(plan => ({ ...plan, planType: 'Split Plan' })),
          ...newCascadeData.map(plan => ({ ...plan, planType: 'Cascade Plan' }))
        ];
        const grouped = groupPlansByStrategy(allPlans, strategies);
        setGroupedData(grouped);
      }, 100);
    }
  }
  return (
    <>
      {canStart ? (
        canShow ? (
          <>
            <div className="flex justify-end items-center mb-2">
              <div className="flex">
                <>
                  <Tooltip title={"Reset"}>
                    <Button
                      disabled={!filterStatus}
                      onClick={() => resetFilter()}
                      className="mr-3"
                    >
                      <UndoOutlined />
                    </Button>
                  </Tooltip>
                  <Tooltip title={"Filter"}>
                    <Badge
                      count={
                        filterStatus ? (
                          <CheckCircleFilled style={{ color: "#f5222d" }} />
                        ) : (
                          0
                        )
                      }
                    >
                      <Button onClick={() => setFilterModal(true)}>
                        <FilterOutlined />
                      </Button>
                    </Badge>
                  </Tooltip>
                </>
                {permissionAbility(userLevel, "plans", "split") && (
                  <Button className="ml-3" onClick={() => setAddModal(true)}>
                    Split Plan
                  </Button>
                )}
              </div>
            </div>
            {/* Tree view disabled - always show table view */}
            <div className="mb-5">
                {groupedData.length > 0 ? (
                  <Collapse
                    defaultActiveKey={groupedData.map(group => group.key)}
                    expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
                    ghost
                  >
                    {groupedData.map(group => (
                      <Panel
                        header={
                          <Space>
                            <FolderOutlined style={{ color: group.type === 'ungrouped' ? '#8c8c8c' : '#722ed1' }} />
                            <Text strong>{group.strategy.name}</Text>
                            <Tag color={group.type === 'ungrouped' ? 'default' : 'purple'}>
                              {group.plans.length} plans
                            </Tag>
                          </Space>
                        }
                        key={group.key}
                      >
                        {group.type !== 'ungrouped' && group.strategy.description && (
                          <div style={{ marginBottom: '16px' }}>
                            <Text type="secondary">{group.strategy.description}</Text>
                          </div>
                        )}
                        <SplitPlanTreeCards
                          data={group.plans}
                          loading={false}
                          loadMore={() => { }}
                          startAt={0}
                          setStartAt={() => { }}
                          totalSplitPlanCount={group.plans.length}
                          handleDelete={(planId) => handleDelete(planId)}
                          handleEdit={(editedPlan) => handleEdit(editedPlan)}
                          onParentRefresh={props.onParentRefresh}
                          onTargetUpdate={(planId, target) => {
                            // Update the specific plan's target in both data arrays
                            const updatePlanInArray = (planArray, setPlanArray) => {
                              const newArray = planArray.map(plan => 
                                plan.planId === planId 
                                  ? { ...plan, targetAchieved: target, performance: ((target / plan.target) * 100).toFixed(2) }
                                  : plan
                              );
                              setPlanArray(newArray);
                              return newArray;
                            };

                            const newData = updatePlanInArray(data, setData);
                            const newCascadeData = updatePlanInArray(cascadeData, setCascadeData);
                            
                            // Re-group data with updated values
                            const allPlans = [
                              ...newData.map(plan => ({ ...plan, planType: 'Split Plan' })),
                              ...newCascadeData.map(plan => ({ ...plan, planType: 'Cascade Plan' }))
                            ];
                            const grouped = groupPlansByStrategy(allPlans, strategies);
                            setGroupedData(grouped);
                            
                            // Notify parent if needed
                            if (props.onParentRefresh) {
                              props.onParentRefresh();
                            }
                          }}
                        />
                      </Panel>
                    ))}
                  </Collapse>
                ) : (
                  <SplitPlanTreeCards
                    data={data.map(plan => ({ ...plan, planType: 'Split Plan' }))}
                    loading={tableLoading}
                    loadMore={(num) => loadMore(num)}
                    startAt={startAt}
                    setStartAt={(num) => setStartAt(num)}
                    totalSplitPlanCount={totalSplitPlanCount}
                    handleDelete={(planId, planType) => handleDelete(planId, planType)}
                    handleEdit={(editedPlan) => handleEdit(editedPlan)}
                    onParentRefresh={props.onParentRefresh}
                    onTargetUpdate={(planId, target) => {
                      // Update the specific plan's target in both data arrays
                      const updatePlanInArray = (planArray, setPlanArray) => {
                        const newArray = planArray.map(plan => 
                          plan.planId === planId 
                            ? { ...plan, targetAchieved: target, performance: ((target / plan.target) * 100).toFixed(2) }
                            : plan
                        );
                        setPlanArray(newArray);
                        return newArray;
                      };

                      const newData = updatePlanInArray(data, setData);
                      // Removed cascade data update - only split plans in Plans tab
                      
                      // Re-group data with updated values
                      const allPlans = newData.map(plan => ({ ...plan, planType: 'Split Plan' }));
                      const grouped = groupPlansByStrategy(allPlans, strategies);
                      setGroupedData(grouped);
                      
                      // Notify parent if needed
                      if (props.onParentRefresh) {
                        props.onParentRefresh();
                      }
                    }}
                  />
                )}
                {/* Load More Button for grouped view */}
                {groupedData.length > 0 && !tableLoading && totalSplitPlanCount > data?.length && (
                  <div className="flex items-center justify-center mt-4">
                    <Button
                      onClick={() => {
                        setStartAt(startAt + limit);
                        loadMore(startAt + limit);
                      }}
                    >
                      Load more
                    </Button>
                  </div>
                )}
              </div>
          </>
        ) : (
          showEmptyPlans()
        )
      ) : (
        <PageLoader />
      )}
      <SplitPlanModal
        visible={addModal}
        handleCancel={() => setAddModal(false)}
        handleOk={async (values) => {
          // First refresh strategies in case new ones were created during plan creation
          await fetchStrategies();
          
          const newData = [values, ...data]; // Use spread to avoid mutation
          setData(newData);
          setStartAt(startAt + 1);
          totalSplitPlanCount === 0 && setCanShow(true);
          setTotalSplitPlanCount(totalSplitPlanCount + 1);
          
          // Re-group data after adding new plan with updated strategies
          setTimeout(() => {
            const allPlans = [
              ...newData.map(plan => ({ ...plan, planType: 'Split Plan' })),
              ...cascadeData.map(plan => ({ ...plan, planType: 'Cascade Plan' }))
            ];
            const grouped = groupPlansByStrategy(allPlans, strategies);
            setGroupedData(grouped);
          }, 100);
        }}
        plan={props.data}
      />

      <CascadePlanModal
        visible={addCascadeModal}
        handleCancel={() => setAddCascadeModal(false)}
        handleOk={async (values) => {
          // First refresh strategies in case new ones were created during plan creation
          await fetchStrategies();
          
          const newCascadeData = [values, ...cascadeData]; // Use spread to avoid mutation
          setCascadeData(newCascadeData);
          setStartAt(startAt + 1);
          totalSplitPlanCount === 0 && setCanShow(true);
          setTotalSplitPlanCount(totalSplitPlanCount + 1);
          
          // Re-group data after adding new cascade plan with updated strategies
          setTimeout(() => {
            const allPlans = [
              ...data.map(plan => ({ ...plan, planType: 'Split Plan' })),
              ...newCascadeData.map(plan => ({ ...plan, planType: 'Cascade Plan' }))
            ];
            const grouped = groupPlansByStrategy(allPlans, strategies);
            setGroupedData(grouped);
          }, 100);
        }}
        plan={props.data}
      />

      <SplitsFilterModal
        visible={filterModal}
        handleCancel={() => setFilterModal(false)}
        handleOk={(values) => {
          getFilteredPlans(values, 0, true);
        }}
        filterBy={filterBy}
      />
    </>
  );
}

export default TopPlanPlans;
